import { usePaymentReturnStatus } from "@/hooks/usePaymentReturnStatus";
import { useSubscriptionAbility } from "@/hooks/useSubscriptionAbility";
import { useApolloClient } from "@apollo/client/react";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import {
  MyUserWorkspaceDocument,
  WorkspaceFreeTrialDocument,
} from "@repo/graphql/documents/workspace/query.generated";
import { SubscriptionAbilities } from "@repo/graphql/gql/graphql";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useRef, useState } from "react";

type CheckoutReturn = {
  /** A paid return is still waiting for the provider's confirmation. */
  verifying: boolean;
};

type ConfirmedPlan = { name: string | null };

const showToast = (action: "success" | "warning", message: string) => {
  MontuToastProvider.show({
    placement: "bottom",
    duration: 6000,
    render: ({ id }) => (
      <Toast nativeID={`toast-${id}`} action={action} variant="solid">
        <ToastDescription>{message}</ToastDescription>
      </Toast>
    ),
  });
};

/**
 * Handles landing back on a campaign-publishing page after a plan checkout
 * started there (`createSubscriptionCheckout(returnPath:)`): the backend's
 * return leg appends `?checkout=<reference>`. The param is read once and
 * cleared so a reload does not re-verify; the payment is then polled, and once
 * it is confirmed the workspace's abilities are refetched so publishing turns
 * on without a reload. The toast says whether the new plan actually includes
 * publishing campaigns, from the refetched ability rather than assuming it.
 */
export const useCheckoutReturn = (): CheckoutReturn => {
  const client = useApolloClient();
  const { can: canCreateCampaign } = useSubscriptionAbility(
    SubscriptionAbilities.CreateCampaign,
  );
  const { checkout } = useLocalSearchParams<{ checkout?: string }>();
  const [reference, setReference] = useState<string | null>(null);
  const [confirmedPlan, setConfirmedPlan] = useState<ConfirmedPlan | null>(
    null,
  );
  const handledRef = useRef(false);
  const toastedRef = useRef(false);

  useEffect(() => {
    if (!checkout) return;
    setReference(checkout);
    router.setParams({ checkout: undefined });
  }, [checkout]);

  const { status, planName } = usePaymentReturnStatus(
    reference !== null,
    reference ?? undefined,
  );

  useEffect(() => {
    if (handledRef.current) return;
    if (status === "completed") {
      handledRef.current = true;
      const refetch = async () => {
        try {
          await client.refetchQueries({
            include: [MyUserWorkspaceDocument, WorkspaceFreeTrialDocument],
          });
        } catch {
          // The cached abilities stay; a reload picks the new plan up.
        }
        setConfirmedPlan({ name: planName });
      };
      refetch();
      return;
    }
    if (status === "unconfirmed" || status === "notFound") {
      handledRef.current = true;
      showToast(
        "warning",
        "Todavía no confirmamos tu pago. Si ya pagaste, puede tardar unos minutos.",
      );
    }
  }, [status, planName, client]);

  useEffect(() => {
    if (!confirmedPlan || toastedRef.current) return;
    toastedRef.current = true;
    const plan = confirmedPlan.name
      ? `Tu plan ${confirmedPlan.name}`
      : "Tu plan";
    if (canCreateCampaign) {
      showToast(
        "success",
        `¡${plan} está activo! Ya puedes publicar tu campaña.`,
      );
      return;
    }
    showToast(
      "warning",
      `${plan} está activo, pero no incluye publicar campañas.`,
    );
  }, [confirmedPlan, canCreateCampaign]);

  // Still "verifying" until the abilities are refetched, so the page never
  // flashes the plan prompt between the payment confirming and the refetch.
  return {
    verifying:
      status === "verifying" ||
      (status === "completed" && confirmedPlan === null),
  };
};
